import React from "react";
import PropTypes from "prop-types";
import { Wrapper } from "./Wrapper";
import { TimerBar } from "./TimerBar";
import { Animation } from "./Animation";
import { Transcript } from "./Transcript";
import { I18nextProvider } from "react-i18next";
import i18next from "./locales/i18next";

import style from "./style.module.scss";

const DEFAULT_RANGE = { min: 0, max: 100 };

const i18n = i18next;

const changeLang = (lang) => {
  i18n.changeLanguage(lang);
};

/**
 * TimerPlayer:
 * Component for Equipment animation dialog/timer player
 */
export function TimerPlayer(props) {
  React.useEffect(() => {
    props.lang && changeLang(props.lang);
  }, [props.lang]);

  const [animeRange, setAnimeRange] = React.useState(DEFAULT_RANGE);

  const setRange = (min, max) => {
    setAnimeRange({ min: min, max: max });
  };

  const tabIndex = props.disabled ? -1 : props.tabIndex;

  const showTranscript = () => !props.disabled && props.showTranscript;

  return (
    <I18nextProvider i18n={i18n}>
      <Wrapper className={style.timerPlayer}>
        {!props.disabled && (
          <Animation
            frame={props.currentFrame}
            isPaused={props.isPaused}
            animationData={props.animationData}
            onChangeFrame={props.onChangeFrame}
            setAnimeRange={setRange}
            onPlaybackComplete={props.onComplete}
            autoPlay={props.onClickPlay}
          />
        )}
        <TimerBar
          onClickPause={props.onClickPause}
          onClickPlay={props.onClickPlay}
          isPaused={props.isPaused}
          currentFrame={props.currentFrame}
          onChangeFrame={props.onChangeFrame}
          min={animeRange.min}
          max={animeRange.max}
          displayTime={props.displayTime}
          disabled={props.disabled}
          tabIndex={tabIndex}
          onComplete={props.onComplete}
        />
        {showTranscript() && (
          <Transcript tabIndex={tabIndex} transcript={props.transcript} />
        )}
      </Wrapper>
    </I18nextProvider>
  );
}

TimerPlayer.propTypes = {
  /**
   * Disabled is a boolen which stores the component is working or not
   */
  disabled: PropTypes.bool,
  /**
   * Which is used for keyboard navigation
   */
  tabIndex: PropTypes.oneOf([0, -1]),
  /**
   * CurrentFrame stores the data of the animation shows at the moment
   */
  currentFrame: PropTypes.number,
  /**
   * It is used to understand wheather the animation is running or not
   */
  isPaused: PropTypes.bool,
  /**
   * data for animation component
   */
  animationData: PropTypes.shape({
    /**
     * Array of animation frames
     */
    animation: PropTypes.object,
    /**
     * isPath is used to identify wheather the data is given as path or file
     */
    isPath: PropTypes.bool,
  }),
  /**
   * onChangeFrame is function used to change the frame of animation
   */
  onChangeFrame: PropTypes.func,
  /**
   * onClickPause is used to pause the animation
   */
  onClickPause: PropTypes.func,
  /**
   * onClickPlay is used to play the animation
   */
  onClickPlay: PropTypes.func,
  /**
   * it shows the date and time in the range sliders
   */
  displayTime: PropTypes.instanceOf(Date),
  /**
   * ShowTranscript is a boolen which denotes the subtitle is visible or not
   */
  showTranscript: PropTypes.bool,
  /**
   * path containing transcript content data
   */
  transcript: PropTypes.string,
  /**
   * callback function to invoke on video playback is complete
   */
  onComplete: PropTypes.func,
};

TimerPlayer.defaultProps = {
  showTranscript: false,
  disabled: false,
  tabIndex: 0,
};
